import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useApi } from "/src/helpers/Hooks/useApi";

// Same tile server the Umbraco site uses for its Leaflet maps (OpenStreetMap data, Olympic style).
const TILE_URL = "https://maps.astrondo.com/olympic/{z}/{x}/{y}.png";
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Pins are divIcons (no image assets to bundle). Three distinct glyphs so they can't be confused:
//  - selected pick-up: solid brand-blue teardrop pin
//  - alternative pick-ups: small white dot with a grey ring
//  - the customer's hotel: orange rounded square with a bed
const selectedIcon = L.divIcon({
    className: "pickup-map-pin pickup-map-pin--selected",
    iconSize: [30, 38],
    iconAnchor: [15, 38],
    tooltipAnchor: [0, -36],
    html: `<svg viewBox="0 0 24 30" width="30" height="38" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 1C6.5 1 2 5.4 2 10.9 2 18.3 12 29 12 29s10-10.7 10-18.1C22 5.4 17.5 1 12 1z"
              fill="#1a8fd9" stroke="#0e7fc7" stroke-width="1.5"/>
        <circle cx="12" cy="11" r="4" fill="#ffffff"/>
    </svg>`,
});

const alternativeIcon = L.divIcon({
    className: "pickup-map-pin pickup-map-pin--alternative",
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    tooltipAnchor: [0, -10],
    html: `<svg viewBox="0 0 18 18" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="9" r="7.5" fill="#ffffff" stroke="#4a5670" stroke-width="2"/>
        <circle cx="9" cy="9" r="2.5" fill="#4a5670"/>
    </svg>`,
});

const hotelIcon = L.divIcon({
    className: "pickup-map-hotel",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    tooltipAnchor: [0, -16],
    html: `<svg viewBox="0 0 30 30" width="30" height="30" xmlns="http://www.w3.org/2000/svg">
        <rect x="1.5" y="1.5" width="27" height="27" rx="8" fill="#f39b2f" stroke="#ffffff" stroke-width="2"/>
        <path d="M7 20v-8h2v4h5v-3h7a2 2 0 0 1 2 2v5h-2v-2H9v2H7z" fill="#ffffff"/>
        <circle cx="11.5" cy="10.5" r="1.8" fill="#ffffff"/>
    </svg>`,
});

// True when the walk from the hotel is too long to quote: the server times the drive as well and
// only then (over 25 minutes on foot), so the presence of driveMinutes is the whole rule.
export const isDrive = (p) => !!p && p.driveMinutes != null;

// "12 min walk · 900 m", or "31 min by car · 18.4 km" once the walk is over 25 minutes.
export const walkLabel = (p) => {
    if (!p) return null;
    const drive = isDrive(p);
    const minutes = drive ? p.driveMinutes : p.walkMinutes;
    if (minutes == null) return null;
    const metres = drive ? p.driveMetres : p.walkMetres;
    const km = metres != null ? (metres >= 1000 ? `${(metres / 1000).toFixed(1)} km` : `${metres} m`) : null;
    return `${minutes} min ${drive ? "by car" : "walk"}${km ? ` · ${km}` : ""}`;
};

// Fits the view to every point, the hotel and the drawn route whenever they change.
const FitPoints = ({ points, selectedId, hotel, route }) => {
    const map = useMap();
    useEffect(() => {
        const coords = points.map((p) => [p.latitude, p.longitude]);
        if (hotel) coords.push([hotel.latitude, hotel.longitude]);
        if (route?.length) coords.push(...route);
        if (coords.length === 1) {
            map.setView(coords[0], 15);
            return;
        }
        map.fitBounds(coords, { padding: [32, 32], maxZoom: 16 });
    }, [map, points.map((p) => p.pickupPointId).join(","), selectedId, hotel?.latitude, hotel?.longitude, route?.length]);
    return null;
};

// Tooltips are clipped by the map box, so each label opens away from whichever edge its pin is
// near (below when near the top, sideways near the sides). Recomputed on pan/zoom.
const useLabelDirections = (positions) => {
    const map = useMap();
    const [dirs, setDirs] = useState({});
    const compute = () => {
        const size = map.getSize();
        const next = {};
        positions.forEach(({ key, lat, lng }) => {
            const pt = map.latLngToContainerPoint([lat, lng]);
            // Labels wrap at ~220px (index.scss), so ~130px either side of the pin must be free for
            // a top/bottom label; otherwise open it towards whichever side has more room.
            const side = pt.x < 130 || pt.x > size.x - 130 ? (pt.x <= size.x / 2 ? "right" : "left") : null;
            next[key] = side || (pt.y < 84 ? "bottom" : "top");
        });
        setDirs(next);
    };
    useMapEvents({ moveend: compute, zoomend: compute, resize: compute });
    useEffect(compute, [positions.map((x) => `${x.key}:${x.lat},${x.lng}`).join("|")]);
    return dirs;
};

// Routes are memoised per (hotel, point) for the page's lifetime — the same walk is asked for
// again every time the map re-renders or a customer flips between points.
const routeCache = new Map();

const Pins = ({ plotted, selected, hotelPin, onSelect }) => {
    const positions = plotted.map((p) => ({ key: p.pickupPointId, lat: p.latitude, lng: p.longitude }));
    if (hotelPin) positions.push({ key: "hotel", lat: hotelPin.latitude, lng: hotelPin.longitude });
    const dirs = useLabelDirections(positions);
    return (
        <>
            {hotelPin && (
                <Marker position={[hotelPin.latitude, hotelPin.longitude]} icon={hotelIcon} zIndexOffset={500}>
                    <Tooltip key={dirs.hotel || "top"} direction={dirs.hotel || "top"} opacity={0.95}>
                        <span className="font-semibold">Your hotel</span>{hotelPin.name ? ` · ${hotelPin.name}` : ""}
                    </Tooltip>
                </Marker>
            )}
            {plotted.map((p) => {
                const isSel = selected && p.pickupPointId === selected.pickupPointId;
                const walk = walkLabel(p);
                const dir = dirs[p.pickupPointId] || "top";
                return (
                    <Marker key={p.pickupPointId} position={[p.latitude, p.longitude]}
                            icon={isSel ? selectedIcon : alternativeIcon} zIndexOffset={isSel ? 1000 : 0}
                            eventHandlers={onSelect ? { click: () => onSelect(p.pickupPointId) } : undefined}>
                        {/* keyed on direction: Leaflet binds it once, so a change re-creates the tooltip */}
                        <Tooltip key={dir} direction={dir} opacity={0.95}>
                            <span className="font-semibold">{p.location}</span>{p.time ? ` · ${p.time}` : ""}
                            {walk && <span className="block text-[0.6875rem] text-content_2">{walk}</span>}
                            {!isSel && onSelect && <span className="block text-[0.6875rem] text-emp_dark">Click to pick up here</span>}
                        </Tooltip>
                    </Marker>
                );
            })}
        </>
    );
};

/**
 * Embedded map of an excursion's pick-up points (Leaflet on the maps.astrondo.com tiles).
 * Only points with coordinates are plotted. Labels show on hover; clicking a pin selects that
 * pick-up point (onSelect). When the customer's hotel is known the walking route from it to the
 * selected point is drawn (Astrondo map API via /api/excursion/route).
 */
const PickupMap = ({ points, selectedId, hotel, onSelect, height = 220, className = "" }) => {
    const api = useApi();
    const plotted = (points || []).filter((p) => p.latitude != null && p.longitude != null);
    const hotelPin = hotel && hotel.latitude != null && hotel.longitude != null ? hotel : null;
    const selected = plotted.find((p) => p.pickupPointId === selectedId) || null;
    const [route, setRoute] = useState(null);

    // Points too far to walk are routed (and drawn) by car instead.
    const profile = isDrive(selected) ? "car" : "foot";
    const routeKey = hotelPin && selected
        ? `${profile}:${hotelPin.latitude},${hotelPin.longitude}->${selected.latitude},${selected.longitude}` : null;
    useEffect(() => {
        let live = true;
        if (!routeKey) { setRoute(null); return undefined; }
        if (routeCache.has(routeKey)) { setRoute(routeCache.get(routeKey)); return undefined; }
        setRoute(null);
        (async () => {
            const r = await api.excursionRoute(hotelPin, selected, profile);
            const result = r?.coordinates?.length ? r : null;
            routeCache.set(routeKey, result);
            if (live) setRoute(result);
        })();
        return () => { live = false; };
    }, [routeKey]);

    if (plotted.length === 0 && !hotelPin) return null;
    const focus = selected || hotelPin || plotted[0];
    const centre = [focus.latitude, focus.longitude];
    const routeLine = route?.coordinates || null;
    return (
        // isolate + z-0: Leaflet's panes/controls sit at z-index 400–1000, which would otherwise
        // leak through and paint over modals (z-50) opened above the page.
        <div className={`rounded-[0.625rem] overflow-hidden border border-content_5 relative isolate z-0 ${className}`} style={{ height }}>
            <MapContainer center={centre} zoom={15}
                          scrollWheelZoom={false} dragging={!L.Browser.mobile} tap={!L.Browser.mobile}
                          style={{ height: "100%", width: "100%" }} attributionControl={true}>
                <TileLayer attribution={ATTRIBUTION} url={TILE_URL} />
                <FitPoints points={plotted} selectedId={selected?.pickupPointId} hotel={hotelPin} route={routeLine} />
                {routeLine && (
                    <>
                        <Polyline positions={routeLine} pathOptions={{ color: "#ffffff", weight: 7, opacity: 0.9 }} />
                        <Polyline positions={routeLine} pathOptions={{ color: "#1a8fd9", weight: 4, opacity: 1, dashArray: "1 8", lineCap: "round" }} />
                    </>
                )}
                <Pins plotted={plotted} selected={selected} hotelPin={hotelPin} onSelect={onSelect} />
            </MapContainer>
            {route && selected && (
                <div className="absolute left-2 bottom-2 z-[400] bg-white/95 rounded-lg px-2.5 py-1.5 text-[0.75rem] text-primary shadow-sm pointer-events-none">
                    <span className="font-semibold">{route.durationMinutes} min {route.profile === "car" ? "by car" : "walk"}</span>
                    <span className="text-content_2"> from your hotel to {selected.location}</span>
                </div>
            )}
        </div>
    );
};

export default PickupMap;
